import React, { useCallback, useMemo, useRef, useState } from 'react';
import { CardActionsMenu } from '@/shared/components/ui';
import TagsDialog from '@/modules/chat/components/TagsDialog';
import { useKanbanDensity } from '@/shared/components/kanban/KanbanDensityContext';
import type { PopulatedLead, KanbanLeadCard } from '../types';
import { buildLeadCardActions } from './leadCardActions';
import {
  buildLeadCardViewModel, LEAD_CARD_KEBAB, LEAD_CARD_SHELL,
  type LeadCardSource, type LeadStepThreshold,
} from './leadCardHelpers';
import { LeadCardCompact, LeadCardDetailed, LeadCardNormal } from './LeadCardVariants';

export type LeadCardAction =
  | 'view' | 'edit' | 'transfer' | 'delete' | 'qualify' | 'disqualify'
  | 'win' | 'lose' | 'snooze' | 'unsnooze';

export interface LeadCardProps {
  /** Dados do lead — objeto completo (table/dashboard) ou card enxuto do kanban. */
  lead: PopulatedLead | KanbanLeadCard;
  onAction?: (action: LeadCardAction, lead: PopulatedLead | KanbanLeadCard) => void;
  showAssignee?: boolean;
  isSelected?: boolean;
  /** Limites resolvidos da etapa (board → tarefa 10) — alimentam "9d · limite 5d". */
  stepThreshold?: LeadStepThreshold;
}

/**
 * Card de lead do Kanban CRM. A DENSIDADE escolhe qual dos três desenhos
 * aprovados renderiza (mockups `comparacao.html`, lado Corrigido):
 *   detailed → 01 Ficha de dois campos · normal → 02 Coluna de pessoas
 *   compact  → 03 Linha viva
 * Esta casca é dona de TODO o comportamento (menu, diálogo de tags, contêiner);
 * os corpos em `LeadCardVariants.tsx` são funções puras do view-model.
 */
export const LeadCard: React.FC<LeadCardProps> = ({
  lead, onAction, showAssignee = false, isSelected = false, stepThreshold,
}) => {
  const density = useKanbanDensity();
  const [isTagsDialogOpen, setIsTagsDialogOpen] = useState(false);
  const detachTagsListener = useRef<(() => void) | null>(null);

  const vm = useMemo(
    () => buildLeadCardViewModel(lead as LeadCardSource, stepThreshold),
    [lead, stepThreshold],
  );

  /**
   * Ouvinte NATIVO em fase de captura no `[data-tags-area]`: dispara antes da
   * delegação do React e antes do `onClick` do card na coluna
   * (KanbanColumn.tsx:586-589), para o clique nos chips abrir o diálogo de tags
   * em vez do lead.
   *
   * É um CALLBACK REF, e não `useEffect` com lista de dependências, porque o
   * gatilho real é o nó entrar e sair do DOM — e ele entra e sai por três
   * motivos independentes: a densidade muda (só o corpo de Detalhes desenha
   * chips), o lead fica sem nenhuma tag (`vm.tags.length > 0`) e a coluna
   * virtualizada remonta a linha. Uma lista de dependências só cobre os motivos
   * que alguém lembrou de listar — e compara VALORES, então um nó novo com o
   * mesmo `length` não reinstalaria o ouvinte. O callback ref é chamado pelo
   * próprio React a cada troca de identidade do nó (com `null` antes de soltar o
   * antigo), o que torna "área remontou = ouvinte reinstalado" um invariante em
   * vez de uma lembrança. Também cobre o desmonte do card inteiro.
   */
  const tagsAreaRef = useCallback((el: HTMLSpanElement | null) => {
    detachTagsListener.current?.();
    detachTagsListener.current = null;
    if (!el) return;
    const handler = (e: Event) => {
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();
      setIsTagsDialogOpen(true);
    };
    el.addEventListener('click', handler, true);
    detachTagsListener.current = () => el.removeEventListener('click', handler, true);
  }, []);

  const variantProps = { vm, showAssignee, tagsAreaRef };

  return (
    <div
      data-testid={`lead-card-${lead.id}`}
      data-density={density}
      className="group relative cursor-pointer transition-colors"
      style={{
        ...LEAD_CARD_SHELL[density],
        minWidth: 0,
        borderRadius: 12,
        backgroundColor: isSelected ? 'var(--theme-surface-selected)' : 'var(--theme-card-background)',
        border: `1px solid ${isSelected ? 'var(--theme-primary)' : 'var(--theme-card-border)'}`,
        color: 'var(--theme-card-text)',
        fontSize: 13,
        // Acento lateral por DENTRO (inset): borda de 2px comeria 1px da caixa e
        // desalinharia o card acentuado dos irmãos (cards-v3.css §5).
        ...(vm.accentColor ? { boxShadow: `inset 2px 0 0 ${vm.accentColor}` } : {}),
      }}
      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.borderColor = 'var(--theme-input-border)'; }}
      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.borderColor = 'var(--theme-card-border)'; }}
    >
      {/* `.tk-more`: fundo sólido, invisível até o hover do card */}
      {onAction && (
        <div
          className="absolute opacity-0 group-hover:opacity-100 transition-opacity rounded-[7px]"
          style={{ ...LEAD_CARD_KEBAB[density], backgroundColor: 'var(--theme-card-background)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <CardActionsMenu
            size="sm"
            actions={buildLeadCardActions(lead, onAction)}
            data-testid={`lead-actions-button-${lead.id}`}
          />
        </div>
      )}

      {density === 'detailed' && <LeadCardDetailed {...variantProps} />}
      {density === 'normal' && <LeadCardNormal {...variantProps} />}
      {density === 'compact' && <LeadCardCompact {...variantProps} />}

      {lead.contact?.id && (
        <TagsDialog
          contactId={lead.contact.id}
          contactName={lead.contact.name}
          isOpen={isTagsDialogOpen}
          onClose={() => setIsTagsDialogOpen(false)}
        />
      )}
    </div>
  );
};
